"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
// import Button from "@mui/material/Button";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { FormBuilder } from "../../components/form-builder";
import { FormRenderer } from "../../components/form-renderer";
import { AdminDashboard } from "../../components/admin-dashboard";
import { FormReport } from "../../components/form-report"; // Added FormReport import
import { AuthForms } from "../../components/auth-forms";
import { useAuth } from "utils/AuthContext";
import { Plus, FileText, BarChart3, ExternalLink, Eye, LogOut, User, Edit } from "lucide-react";
import "./Homepage.css";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Snackbar, Alert, Box, Pagination, Tooltip } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Chip from "@mui/material/Chip";

export default function HomePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("builder");
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [editingForm, setEditingForm] = useState(null);
  const [viewingFormReport, setViewingFormReport] = useState(null);
  const [fillingForm, setFillingForm] = useState(null);
  const [previewForm, setPreviewForm] = useState(null);
  const [state, setState] = useState({
    open: false,
    severity: "success",
    vertical: "top",
    horizontal: "right",
    message: "",
  });

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 9 });
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: null, title: "" });

  const handleFillForm = (form) => {
    const formattedTitle = form.title?.replaceAll(" ", "-") || "form";
    const userSubmission = form.submissions?.find((s) => s.created_by === meData?.user_id) || form.submissions?.[0];

    if (userSubmission) {
      navigate(`/mission-tracker/edit/${form.id}/${userSubmission.created_by}/${userSubmission.submission_id}`);
    } else {
      navigate(`/mission-tracker/add/${form.id}--${formattedTitle}`);
    }
  };

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data;
  };
  const handleClose = () => setState({ ...state, open: false });

  const {
    data: meData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["me"], // Unique key for the query
    queryFn: me, // The async function to run
    retry: false,
  });

  useEffect(() => {
    if (meData && meData.user_id !== 1) {
      setActiveTab("forms"); // default to "My Forms" for non-admins
    }
  }, [meData]);

  useEffect(() => {
    if (!meData?.user_id) return;
    const fetchForms = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/allforms/all`, {
          params: {
            user_id: meData?.user_id,
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
          },
          withCredentials: true,
        });
        setForms(res.data.data || []);
        setRowCount(res.data.totalCount || 0);
      } catch (err) {
        console.error("Error fetching forms:", err);
      }
    };

    fetchForms();
  }, [meData?.user_id, pagination.pageIndex, pagination.pageSize]);

  const loadForms = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/allforms/all`, {
        withCredentials: true,
      });
      setForms(res.data.data || []);
    } catch (err) {
      console.error("Error loading forms:", err);
    }
  };

  useEffect(() => {
    const fetchFormById = async () => {
      if ((viewMode === "fill" || viewMode === "preview") && selectedForm) {
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/forms/${selectedForm}`, {
            withCredentials: true,
          });
          if (viewMode === "fill") {
            setFillingForm(res.data.data);
          } else if (viewMode === "preview") {
            setPreviewForm(res.data.data);
          }
        } catch (err) {
          console.error("Error fetching form by ID:", err);
        }
      }
    };

    fetchFormById();
  }, [viewMode, selectedForm]);

  const handleSaveForm = async () => {
    await loadForms(); // always refresh from DB
    setEditingForm(null);
    setActiveTab("forms");
  };

  const handleFormSubmission = async (formId, submissionData) => {
    const payload = {
      form_id: formId,
      title: fillingForm.title,
      description: fillingForm.description,
      submittedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      submitted_by: meData?.user_id,
      district: meData?.district,
      submissions: [submissionData],
    };

    try {
      setLoading(true);

      const res = await axios.post(`${process.env.REACT_APP_API_URL}/submissions/add`, payload, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      // console.log("resres", res);

      if (res.data.status === 200) {
        setState({
          ...state,
          open: true,
          severity: "success",
          message: res.data.message, // ✅ Correct path
        });

        setViewMode("list");
        setSelectedForm(null);

        setForms((prevForms) =>
          prevForms.map((form) =>
            form.id === formId
              ? {
                ...form,
                submissions: [...(form.submissions || []), submissionData],
              }
              : form
          )
        );
      }
    } catch (err) {
      console.error("❌ Error submitting form:", err);

      if (err.response) {
        setState({
          ...state,
          open: true,
          severity: "error",
          message: err.response.data.message || "Failed to submit form",
        });
      } else {
        setState({
          ...state,
          open: true,
          severity: "error",
          message: "Server error. Please try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // const handleEditForm = (form) => {
  //   setEditingForm(form);
  //   setActiveTab("builder");
  //   setViewingFormReport(null); // Clear form report view when editing
  // };

  const handleEditForm = async (formId) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/forms/${formId}`, {
        withCredentials: true,
      });

      const fullForm = res.data.data; // full form with fields etc.
      setEditingForm(fullForm);
      setActiveTab("builder");
      setViewingFormReport(null);
    } catch (err) {
      console.error("Error fetching form for edit:", err);
    }
  };

  const handleCancelEdit = () => {
    setEditingForm(null);
    setActiveTab("forms");
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}/forms/delete/${deleteTarget.id}`,
        { withCredentials: true }
      );

      if (res.data.status === 200) {
        setState({
          ...state,
          open: true,
          severity: "success",
          message: res.data.message,
        });

        // refresh list
        setTimeout(async () => {
          await loadForms();
        }, 1000);
      }

      setShowDeleteDialog(false);
    } catch (err) {
      console.error("Error deleting form:", err);
      setState({
        ...state,
        open: true,
        severity: "error",
        message: err.response?.data?.message || "Failed to delete form",
      });
      setShowDeleteDialog(false);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (formId, newStatus) => {
    try {
      setLoading(true);
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/forms/status/${formId}`,
        { status: newStatus }, // send the new status
        { withCredentials: true }
      );

      if (res.data.status === 200) {
        setState({
          ...state,
          open: true,
          severity: "success",
          message: res.data.message,
        });

        setTimeout(async () => {
          await loadForms(); // refresh list
        }, 1000);
      }
      setShowDeleteDialog(false);
    } catch (err) {
      console.error("Error updating form status:", err);
      setState({
        ...state,
        open: true,
        severity: "error",
        message: err.response?.data?.message || "Failed to update form status",
      });
      setShowDeleteDialog(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubmission = (formId, submissionIndex, updatedSubmission) => {
    setForms((prevForms) =>
      prevForms.map((form) =>
        form.id === formId
          ? {
            ...form,
            submissions: form.submissions.map((submission, index) =>
              index === submissionIndex ? updatedSubmission : submission
            ),
          }
          : form
      )
    );
  };

  const handleViewFormReport = async (formId) => {
    if (!meData?.user_id) {
      console.warn("meData not loaded yet");
      return;
    }

    try {
      let url = "";

      if (meData.user_id == 1) {
        url = `${process.env.REACT_APP_API_URL}/allforms/${formId}`;
      } else {
        url = `${process.env.REACT_APP_API_URL}/submissions/${formId}/${meData.user_id}`;
      }

      const res = await axios.get(url, { withCredentials: true });

      // ✅ Only set the form details here
      setViewingFormReport(res.data.data);

      setActiveTab("analytics");
    } catch (err) {
      console.error("Error fetching form report:", err);
    }
  };

  const handleBackFromReport = () => {
    setViewingFormReport(null);
  };

  return (
    <div className="homepage">
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={state.open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity={state.severity} variant="filled">
          {state.message}
        </Alert>
      </Snackbar>

      {/* <div className="homepage-header" style={{ textAlign: "center" }}>
        <h1>Form Builder</h1>
      </div> */}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="tabs-container">
        {meData?.role_id !== 3 && (
          <TabsList className="tabs-list">
            {meData?.user_id === 1 && (
              <TabsTrigger
                value="builder"
                className={`tab-btn ${activeTab === "builder" ? "active" : ""}`}
              >
                <Plus className="icon" />
                {editingForm ? "Edit Form" : "Form Builder"} {/* Dynamic tab title */}
              </TabsTrigger>
            )}
            <TabsTrigger value="forms" className={`tab-btn ${activeTab === "forms" ? "active" : ""}`}>
              <FileText className="icon" />
              My Forms
            </TabsTrigger>
          </TabsList>
        )}

        {meData?.user_id === 1 && (
          <TabsContent value="builder">
            <FormBuilder
              onSave={handleSaveForm}
              editingForm={editingForm}
              onCancelEdit={handleCancelEdit}
            />
          </TabsContent>
        )}

        <TabsContent value="forms">
          {viewMode === "list" && (
            <div>
              <div className="flex-between" style={{ marginBottom: "1.5rem" }}>
                {/* <h2 className="section-title">My Forms</h2> */}
                {meData?.user_id === 1 && (
                  <Button
                    className="btn btn-primary"
                    onClick={() => {
                      setEditingForm(null); // reset edit mode
                      setActiveTab("builder"); // go to builder
                    }}
                  >
                    <Plus className="icon" />
                    Create New Form
                  </Button>
                )}
              </div>

              <div className="form-list">
                {forms.length === 0 ? (
                  <Card className="card empty-card">
                    <CardContent>
                      <FileText />
                      {meData?.user_id !== 1 && <p>No forms created yet.</p>}
                      {meData?.user_id === 1 && (
                        <>
                          <p style={{ color: "#000000" }}>
                            No forms created yet. Start building your first form!
                          </p>
                          <Button
                            className="btn btn-primary"
                            onClick={() => setActiveTab("builder")}
                          >
                            Create Form
                          </Button>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {forms.map((form, index) => (
                      <Card
                        key={form.id}
                        className="card hover:shadow-lg transition"
                      >
                        <CardHeader className="card-header">
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <CardTitle className="card-title">
                              <Tooltip title={form.title} arrow>
                                <span>
                                  {form.title?.length > 25
                                    ? form.title.slice(0, 25) + "..."
                                    : form.title}
                                </span>
                              </Tooltip>
                            </CardTitle>

                            {meData?.role_id === 3 && index === 0 && (
                              <Chip
                                label="New"
                                size="small"
                                color="success"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "11px",
                                  height: "22px",
                                }}
                              />
                            )}
                          </div>

                          <CardDescription className="card-desc">
                            <Tooltip title={form.description || ""} arrow>
                              <span>
                                {form.description
                                  ? form.description.length > 35
                                    ? form.description.slice(0, 35) + "..."
                                    : form.description
                                  : "\u00A0"}
                              </span>
                            </Tooltip>
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div>
                            {meData?.user_id === 1 && (
                              <div className="card-meta">
                                <span>{form.fields?.length || 0} fields</span>
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (meData?.user_id === 1) {
                                      navigate(`/mission-tracker/results/${form.id}`);
                                    }
                                  }}
                                  style={{
                                    cursor: meData?.user_id === 1 ? "pointer" : "default",
                                    color: meData?.user_id === 1 ? "#3b82f6" : "inherit",
                                    textDecoration: meData?.user_id === 1 ? "underline" : "none",
                                  }}
                                  title={meData?.user_id === 1 ? "Click to view results" : ""}
                                >
                                  {form.submissions?.length || 0} responses
                                </span>
                              </div>
                            )}

                            <div className="flex-wrap">
                              {/* Added flex-wrap and edit button */}
                              {meData?.user_id === 1 && (
                                <Button
                                  className="btn btn-outline btn-sm"
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditForm(form.id);
                                  }}
                                >
                                  <Edit className="icon" />
                                  Edit
                                </Button>
                              )}
                              {/* <Button
                                className="btn btn-outline btn-sm"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedForm(form.id);
                                  setViewMode("preview");
                                }}
                              >
                                <Eye className="icon" />
                                Preview
                              </Button> */}
                              {meData?.user_id !== 1 && (
                                <Button
                                  className="btn btn-outline btn-sm"
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFillForm(form);
                                  }}
                                >
                                  <ExternalLink className="icon" />
                                  {form.submissions?.length > 0 ? "Update Form" : "Fill Form"}
                                </Button>
                              )}
                              {/* {meData?.user_id === 1 && (
                                <Button
                                  className="btn btn-danger btn-sm"
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => {
                                    setDeleteTarget({ id: form.id, title: form.title });
                                    setShowDeleteDialog(true);
                                  }}
                                >
                                  Delete
                                </Button>
                              )} */}
                              {meData?.user_id === 1 && (
                                <Tooltip
                                  title={
                                    form.status === 2
                                      ? "Click to Inactivate the form"
                                      : "Click to Activate the form"
                                  }
                                  arrow
                                >
                                  <Button
                                    className="btn btn-sm"
                                    variant="contained"
                                    size="small"
                                    style={{
                                      backgroundColor: form.status === 2 ? "#2563eb" : "red",
                                      color: "white",
                                      fontWeight: "600",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteTarget({
                                        id: form.id,
                                        title: form.title,
                                        status: form.status,
                                      });
                                      setShowDeleteDialog(true);
                                    }}
                                  >
                                    {form.status === 2 ? "Active" : "Inactive"}
                                  </Button>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    <Box
                      display="flex"
                      justifyContent="center"
                      width="100%"
                      mt={3}
                      sx={{ gridColumn: "1 / -1" }}
                    >
                      <Pagination
                        count={Math.max(1, Math.ceil(rowCount / pagination.pageSize))}
                        page={pagination.pageIndex + 1} // 1-indexed
                        onChange={(event, value) =>
                          setPagination((prev) => ({ ...prev, pageIndex: value - 1 }))
                        }
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        siblingCount={0}
                        boundaryCount={1}
                      />
                    </Box>
                  </>
                )}
              </div>
            </div>
          )}

          {viewMode === "preview" && previewForm && (
            <div>
              <div className="flex-between">
                <Button
                  className="btn btn-outline"
                  variant="outline"
                  onClick={() => setViewMode("list")}
                >
                  ← Back to Forms
                </Button>
                <h2 className="section-title">Preview: {previewForm.title}</h2>
                {meData?.user_id !== 1 && (
                  <Button
                    className="btn btn-primary"
                    onClick={() => handleFillForm(previewForm)}
                  >
                    Fill This Form
                  </Button>
                )}
              </div>

              <Card className="card-preview">
                <CardHeader className="card-header">
                  <CardTitle className="card-title">{previewForm.title}</CardTitle>
                  {previewForm.description && (
                    <CardDescription className="card-desc">
                      {previewForm.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {previewForm.fields.map((field) => (
                    <div key={field.id} className="field">
                      <label>
                        {field.label}
                        {field.required && <span className="required">*</span>}
                      </label>
                      <div className="field-preview">
                        <span>
                          {field.type.charAt(0).toUpperCase() + field.type.slice(1)} field
                          {field.placeholder && ` - "${field.placeholder}"`}
                        </span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}

          {viewMode === "fill" && fillingForm && (
            <div>
              <div className="flex-between">
                <Button
                  className="btn btn-outline"
                  variant="outline"
                  onClick={() => setViewMode("list")}
                >
                  ← Back to Forms
                </Button>
                <h2 className="section-title">Fill Form: {fillingForm.title}</h2>
              </div>

              <FormRenderer
                form={fillingForm}
                onSubmit={(data) => handleFormSubmission(fillingForm.id, data)}
              />
            </div>
          )}
        </TabsContent>

        <TabsContent value="analytics">
          {viewingFormReport ? (
            <FormReport
              formId={viewingFormReport.id}
              onBack={handleBackFromReport}
              onEditForm={handleEditForm}
              onDeleteForm={handleConfirmDelete}
              onUpdateSubmission={handleUpdateSubmission}
            />
          ) : (
            <AdminDashboard
              forms={forms}
              onEditForm={handleEditForm}
              onDeleteForm={handleConfirmDelete}
              onUpdateSubmission={handleUpdateSubmission}
              onViewFormReport={handleViewFormReport}
            />
          )}
        </TabsContent>
      </Tabs>

      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="xs" // smaller than "sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 2,
            boxShadow: 6,
            position: "relative",
            overflow: "visible",
          },
        }}
      >
        <IconButton
          onClick={() => setShowDeleteDialog(false)}
          sx={{
            position: "absolute",
            top: -14,
            right: -12,
            backgroundColor: "#fff",
            color: "#ff0a54",
            boxShadow: 1,
            zIndex: 1500,
            p: 1,
            "&:hover": {
              backgroundColor: "#ff0a54",
              color: "#fff",
            },
            transition: "transform 0.5s ease",
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogTitle
          sx={{
            background:
              deleteTarget.status === 2
                ? "linear-gradient(45deg, #800000, #191919)"
                : "linear-gradient(45deg, #2e7d32, #191919)",
            color: "white !important",
            fontWeight: "bold",
            fontSize: "16px", // reduced size
            textAlign: "center",
            py: 1.5, // reduced padding
          }}
        >
          Confirm Status Update
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ color: "#000", textAlign: "center", fontSize: "14px" }}>
            Are you sure you want to update the form <strong>{deleteTarget.title}</strong> status to{" "}
            <strong>{deleteTarget.status === 2 ? "Inactive" : "Active"}</strong>?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={() => handleStatusChange(deleteTarget.id, deleteTarget.status === 2 ? 3 : 2)}
            style={{
              background:
                deleteTarget.status === 2
                  ? "linear-gradient(45deg, #800000, #191919)"
                  : "linear-gradient(45deg, #2e7d32, #191919)",
              color: "white",
              fontWeight: "600",
              padding: "8px 16px",
              borderRadius: "8px",
              boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
              transition: "transform 0.2s ease",
              border: "none",
              outline: "none",
              cursor: "pointer",
            }}
            onMouseOver={(e) =>
            (e.currentTarget.style.background =
              deleteTarget.status === 2
                ? "linear-gradient(45deg, #5a0000, #101010)"
                : "linear-gradient(45deg, #1e5722, #101010)")
            }
            onMouseOut={(e) =>
            (e.currentTarget.style.background =
              deleteTarget.status === 2
                ? "linear-gradient(45deg, #800000, #191919)"
                : "linear-gradient(45deg, #2e7d32, #191919)")
            }
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
}
